import { useEffect, useState } from 'react';
import { GetOrderResData, callUpdateVaultApi } from 'requests/RequestPayment';
import { usePayPalHostedFields } from '@paypal/react-paypal-js';
import { Payment, PaymentType, findPaymentByKey, isBack, UserTypeEntity, OrderType } from 'consts/PaymentConst';
import { HttpStatus } from 'consts/HttpStatus';
import classes from 'components/templates/override.module.scss';
import { ApiError } from 'requests/RequestBase';
import { displayCardBrandImage } from 'components/molecules/card-brand/CardBrand';
import { monthSelectList, CARD_MAX_SIZE } from 'consts/CardConst';
import { ApiErrorCode, findErrorMessageByApiError } from 'consts/ApiErrorCode';
import { ReactError } from 'consts/ReactError';

export const useCreditCardForm = (
	order: GetOrderResData,
	transErrorPage: Function,
	redirect_uri: string,
	state: string,
	orderType: OrderType,
	resetOptions: Function,
	setError: React.Dispatch<React.SetStateAction<string>>,
	scrollTop: Function,
	isAgreeSendCardInfo: Boolean,
	defaultIsSavedCard = false,
) => {
	const paymentTokenExists = order?.payment_tokens.length > 0;
	const isPaymentTokenMax = order?.payment_tokens.length === CARD_MAX_SIZE;

	// 支払方法変更時処理
	const [payMethod, setPayMethod] = useState<PaymentType>(Payment.PAYPAL);
	const handleChangeRadioValue = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const payment = findPaymentByKey(Number(event.target.value));
		resetOptions(orderType, payment);
		setPayMethod(payment);
	};
	useEffect(() => {
		if (!order) {
			return;
		}
		if (!order.payment_specify_list || order.payment_specify_list.paypal_hidden_flg !== 1) {
			resetOptions(orderType, Payment.PAYPAL);
			setPayMethod(Payment.PAYPAL);
			return;
		}
		if (order.payment_specify_list.credit_hidden_flg !== 1) {
			resetOptions(orderType, Payment.CREDIT);
			setPayMethod(Payment.CREDIT);
			return;
		}
		resetOptions(orderType, Payment.DEFFER);
		setPayMethod(Payment.DEFFER);
	}, [order]);

	// クレジットカードフォーム表示時処理
	const [isSaveCard, setIsSaveCard] = useState(defaultIsSavedCard);
	const handleChangeCheckValue = (event: React.ChangeEvent<HTMLInputElement>) => {
		setIsSaveCard(event.target.checked);
	};

	// クレジットカード支払いボタン
	const [submitLoading, setSubmitLoading] = useState(false);
	const SubmitPayment = ({ label = '次へ' }) => {
		// HostedFieldsHandler型だとonメソッドが実装されていないためあえてany型で受け取る
		const hostedFields: any = usePayPalHostedFields();

		// クレジットカードのバリデーション(正しい値が入力されるまで支払ボタンをdisabled)
		const [isFormValid, setIsFormValid] = useState(false);
		// 入力済み&ボタン再レンダリング時の初回判定
		useEffect(() => {
			if (!hostedFields) {
				return;
			}
			const state = hostedFields.getState();
			const isValid = Object.keys(state.fields).every((key) => state.fields[key].isValid);
			setIsFormValid(isValid);
		}, []);
		// 入力時都度判定
		hostedFields?.on('validityChange', () => {
			const state = hostedFields.getState();
			const isValid = Object.keys(state.fields).every((key) => state.fields[key].isValid);
			setIsFormValid(isValid);
		});

		// クレジットカードで支払うボタン押下時処理
		const submitHandler = async () => {
			setSubmitLoading(true);
			try {
				await hostedFields
					.submit({
						vault: isSaveCard,
						contingencies: ['SCA_ALWAYS'],
					})
					.then(function (payload) {
						// 3Dセキュア認証キャンセルの場合、支払い不能カードとして処理する
						if (payload.authenticationReason === 'SKIPPED_BY_BUYER' || payload.authenticationReason === 'FAILURE') {
							setError(ReactError.E0014);
							scrollTop();
							throw Error(`3Dsecure Failed`);
						}
					});
				// GakkenIDの時のみvault更新
				if (order.user_type === UserTypeEntity.GAKKEN_ID.key) {
					const updateVaultResult = await callUpdateVaultApi(order.gid_order_no, isSaveCard);
					if (updateVaultResult.status !== HttpStatus.OK) {
						const error = updateVaultResult.data as ApiError;
						if (error.error_code === ApiErrorCode.E1180.code) {
							setSubmitLoading(false);
							setError(findErrorMessageByApiError(error));
							scrollTop();
							return;
						}
						throw Error(`[${error.error_code}] ${error.message}`);
					}
				}
				redirect(Payment.CREDIT);
			} catch (error) {
				console.error(`error: ${JSON.stringify(error)}`);
				setError(ReactError.E0014);
				scrollTop();
			} finally {
				setSubmitLoading(false);
			}
		};
		return (
			<div className="btn gr">
				<button
					onClick={submitHandler}
					className={!isFormValid || !isAgreeSendCardInfo ? classes.disabled : ''}
					disabled={!isFormValid || !isAgreeSendCardInfo}
				>
					{label}
				</button>
			</div>
		);
	};

	const redirect = (payment: PaymentType) => {
		const stateParam = state ? `&state=${encodeURIComponent(state)}` : '';
		if (isBack(payment)) {
			window.location.href = `${redirect_uri}?payment_type=${payment.key}${stateParam}`;
			return;
		}
		const periodicParam = order.periodic_payment_id ? `&periodic_payment_id=${order.periodic_payment_id}` : '';
		window.location.href = `${redirect_uri}?gid_order_no=${order.gid_order_no}&payment_type=${payment.key}${stateParam}${periodicParam}`;
	};

	return {
		isSaveCard,
		payMethod,
		paymentTokenExists,
		handleChangeRadioValue,
		handleChangeCheckValue,
		displayCardBrandImage,
		submitLoading,
		setSubmitLoading,
		SubmitPayment,
		redirect,
		monthSelectList,
		isPaymentTokenMax,
	};
};
